import React, { useContext, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, RadioGroup, RadioButton, Button, IconButton, Icon } from '@cbrebuild/blocks';
import FileUploadAccordian from '../../file-upload-accordian';
import countries from '../../../../data/countries.json';
import states from '../../../../data/states.json';
import Select from '../../../../nucleus/select/select';
import NaicsAutocomplete from '../../../autocomplete/naics-autocomplete';
import ContactAutocomplete from '../../../autocomplete/contact-autocomplete';
import ConfirmationModal from '../../../../nucleus/confirmation-modal/confirmation-modal';
import {
  LeaseAbstractionField,
  LeaseAbstractionFieldType,
} from '../../leaseAbstractionField';

import '../../lease-abstraction.scss';
import LeaseAbstractionContext from '../../contexts/leaseAbstractionContext';
import contactsService from '../../../../services/contacts-service';
import { isPartyClient, isPartyPocRequired } from '../../../../utils/transaction-utils';

const creditRatingData = [
  { value: '', display_name: 'None' },
  { value: 'A', display_name: 'Class A' },
  { value: 'B', display_name: 'Class B' },
  { value: 'C', display_name: 'Class C' },
  { value: 'D', display_name: 'Class D' },
];

const getFilteredStates = (countryName = 'United States') => {
  const found = countries.find(c => c.display_name === countryName);
  return states.filter(s => s.country_id === found?.country_id);
};

const TenantInformation = ({ forceExpanded, isLoaded, onExpand, storageKey }) => {
  const {
    isEnhancedFlow,
    modalFieldState,
    modalFieldChangeHandler,
    individualModalFieldChangeHandler,
    fieldErrorMap,
    validateLeaseAbstractionFields,
    showPageValidation,
    findFieldRef,
    deal,
    company,
    onCompanyChange,
    onCompanyBlur,
    tenantSearchState,
    onTenantSuggestionSelect,
    closeTenantSuggestions,
    triggerTenantSearch,
    isTenantRelocating,
    priorProperty,
    onPriorPropertyChange,
    onPriorPropertyBlur,
    onPriorPropertyCreate,
    onPriorPropertyDelete,
    onPriorSpaceChange,
    onPriorSpaceBlur,
    voucher,
    patchTenantCompany,
    patchTenantContact,
  } = useContext(LeaseAbstractionContext);
  const isEarlyInvoice = !!voucher?.is_early_invoice;
  const showRequiredValidation = showPageValidation && !isEarlyInvoice;

  // Deal contacts — fetched once and shown as quick-fill cards above the POC form (enhanced flow only).
  const [dealContacts, setDealContacts] = useState([]);
  const suppressTenantFirstNameBlurPatchRef = useRef(false);
  const suppressTenantLastNameBlurPatchRef = useRef(false);
  useEffect(() => {
    if (!isLoaded || !isEnhancedFlow || !deal?.id) return;
    contactsService.getContacts({ deal: deal.id, ordering: '-is_primary', page_size: 25 })
      .then(res => setDealContacts((res && res.results) || []))
      .catch(() => setDealContacts([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal?.id, isLoaded]);

  const handleTenantContactSelect = (c) => {
    const phone = c.phone_numbers && c.phone_numbers[0] ? c.phone_numbers[0].number : '';
    const email = c.email_addresses && c.email_addresses[0] ? c.email_addresses[0].address : '';
    modalFieldChangeHandler('multipleFieldNames', {
      tenantPOCFirstName: { value: c.given_name || '' },
      tenantPOCLastName: { value: c.surname || '' },
      tenantPOCPhone: { value: phone },
      tenantPOCEmail: { value: email },
      tenantPOCJobTitle: { value: c.job_title || '' },
    }, 'multiple');
    patchTenantContact({
      given_name: c.given_name || '',
      surname: c.surname || '',
      job_title: c.job_title || '',
      phone_numbers: c.phone_numbers && c.phone_numbers.length ? c.phone_numbers : [],
      email_addresses: c.email_addresses && c.email_addresses.length ? c.email_addresses : [],
    });
  };

  // Local override so the UI responds immediately without waiting for the
  // hook → context → consumer cascade. null = not yet overridden (use context value).
  const [localRelocating, setLocalRelocating] = useState(null);
  const [showRelocationModal, setShowRelocationModal] = useState(false);

  // Sync local state whenever the context value changes (e.g. initial data load).
  // This resets any local override so the authoritative context value takes over.
  const prevContextRelocating = React.useRef(isTenantRelocating);
  if (prevContextRelocating.current !== isTenantRelocating) {
    prevContextRelocating.current = isTenantRelocating;
    if (localRelocating !== null) setLocalRelocating(null);
  }

  const effectiveRelocating = localRelocating !== null ? localRelocating : isTenantRelocating;

  const isTenantOurClient = isPartyClient(deal, 'tenant');
  const isTenantPocRequired = isPartyPocRequired(deal, 'tenant');
  const hasPocFieldValue = fieldName => String(modalFieldState[fieldName]?.value || '').trim().length > 0;
  const getPocFieldError = (fieldName, requiredMessage) => {
    if (!modalFieldState[fieldName]?.value && showPageValidation && isTenantPocRequired) return requiredMessage;
    if (!isTenantPocRequired && !hasPocFieldValue(fieldName)) return '';
    return fieldErrorMap[fieldName] || '';
  };
  const tenantPOCFirstNameError = getPocFieldError('tenantPOCFirstName', 'First Name is required');
  const tenantPOCLastNameError = getPocFieldError('tenantPOCLastName', 'Last Name is required');
  const tenantPOCPhoneError = getPocFieldError('tenantPOCPhone', 'Phone is required');
  const tenantPOCEmailError = getPocFieldError('tenantPOCEmail', 'E-Mail Address is required');

  const handleBusinessTypeChange = (value) => {
    if (value?.naics_code) {
      onCompanyChange({
        target: {
          name: ['voucher_naics_code', 'voucher_naics_category'],
          value: [value.naics_code, value.naics_description],
          type: 'select-one',
          hasMultiple: true,
        },
      });
    } else if (value === '') {
      onCompanyChange({
        target: {
          name: ['voucher_naics_code', 'voucher_naics_category'],
          value: [null, null],
          type: 'select-one',
          hasMultiple: true,
        },
      });
    } else {
      onCompanyChange({ target: { name: 'voucher_naics_category', value, type: 'text' } });
    }
  };

  const handleRelocationChange = ({ target: { value } }) => {
    if (value === 'true') {
      setLocalRelocating(true);
      onPriorPropertyCreate({ location_type_new: false });
    } else {
      setShowRelocationModal(true);
    }
  };

  const priorFilteredStates = getFilteredStates(priorProperty?.country || 'United States');

  return (
    <FileUploadAccordian accordianTitle="Tenant Information" forceExpanded={forceExpanded} onExpand={onExpand} storageKey={storageKey}>
      {/* Company Name — with search-before-create dropdown */}
      <div className="file-modal-group-container">
        <div className="file-modal-group">
          <label className="acc-field-label">{isEnhancedFlow && <span className="acc-required-asterisk">* </span>}Company Name</label>
          <div className="company-name-search-wrapper">
            <input
              type="text"
              className={`acc-input-text${(!modalFieldState.tenantCompanyName?.value && showPageValidation) || fieldErrorMap.tenantCompanyName?.length > 0 ? ' acc-input-error' : ''}`}
              value={modalFieldState.tenantCompanyName?.value || ''}
              placeholder="Company Name"
              onChange={(event) => {
                modalFieldChangeHandler('tenantCompanyName', { value: event.target.value });
                onCompanyChange({ target: { name: 'name', value: event.target.value, type: 'text' } });
              }}
              onFocus={() => { if (triggerTenantSearch) triggerTenantSearch(); }}
              onBlur={(event) => {
                individualModalFieldChangeHandler({
                  child_value: event.target.value,
                  parent_key: 'tenant_buyer_info',
                  child_key: modalFieldState.tenantCompanyName?.trackingID,
                  stateVariable: 'tenantCompanyName',
                });
                setTimeout(() => { if (closeTenantSuggestions) closeTenantSuggestions(); }, 150);
                onCompanyBlur({ target: { name: 'name', value: event.target.value, type: 'text' } });
              }}
            />
            {tenantSearchState?.loading && <div className="company-name-search-loading-bar" />}
            {tenantSearchState?.showSuggestions && tenantSearchState.suggestions.length > 0 && (
              <div className="company-name-search-dropdown">
                {tenantSearchState.suggestions.map(s => (
                  <div
                    key={s.id}
                    className="company-name-search-item"
                    role="option"
                    aria-selected="false"
                    tabIndex={0}
                    onMouseDown={() => {
                      onTenantSuggestionSelect(s);
                      modalFieldChangeHandler('tenantCompanyName', { value: s.name });
                    }}
                  >
                    <span className="company-name-search-item__name">{s.name}</span>
                    {s.address && (
                      <span className="company-name-search-item__address">
                        {[s.address.address, s.address.city, s.address.territory, s.address.postal, s.address.country]
                          .filter(Boolean).join(', ')}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          {((!modalFieldState.tenantCompanyName?.value && showPageValidation) || fieldErrorMap.tenantCompanyName?.length > 0) && (
            <p className="acc-error-message">{fieldErrorMap.tenantCompanyName?.length > 0 ? fieldErrorMap.tenantCompanyName : 'Company Name is required'}</p>
          )}
        </div>
        {/* Extraction chip — enhanced flow only; legacy modal flow shows no chip here */}
        {isEnhancedFlow && modalFieldState.tenantCompanyName?.toolTip?.pageIndex?.length > 0 && (
          <div className="acc-extraction-chip">
            <span className="acc-extraction-chip__icon" aria-hidden="true" />
            {String(modalFieldState.tenantCompanyName.extractedValue || '').trim().toLowerCase()
              !== String(modalFieldState.tenantCompanyName.value || '').trim().toLowerCase()
              ? (
                <button
                  type="button"
                  className="acc-extraction-chip__accept"
                  title="Click to replace with extracted value"
                  onClick={() => {
                    const val = modalFieldState.tenantCompanyName.extractedValue;
                    modalFieldChangeHandler('tenantCompanyName', { value: val });
                    patchTenantCompany({ name: val });
                  }}
                >
                  {modalFieldState.tenantCompanyName.extractedValue}
                </button>
              ) : (
                <span className="acc-extraction-chip__value">{modalFieldState.tenantCompanyName.extractedValue}</span>
              )}
            <button
              type="button"
              className="acc-extraction-chip__page"
              onClick={() => {
                const pageIndex = parseInt(modalFieldState.tenantCompanyName.toolTip.pageIndex, 10);
                if (!Number.isNaN(pageIndex)) findFieldRef('tenantCompanyName', pageIndex);
              }}
            >
              Page {modalFieldState.tenantCompanyName.toolTip.pageIndex}
            </button>
          </div>
        )}
      </div>
      {/* Business Type / Use ID */}
      {isEnhancedFlow && (
        <div className="file-modal-group-full">
          <label className="file-modal-label">{!isEarlyInvoice && <span className="acc-required-asterisk">* </span>}Business Type / Use ID</label>
          <NaicsAutocomplete
            key={company?.id}
            initSearchTerm={company?.voucher_naics_category || ''}
            placeholder="Type to filter business categories"
            onChange={handleBusinessTypeChange}
            errorMessage={!company?.voucher_naics_category && showRequiredValidation ? ' ' : ''}
          />
          {!company?.voucher_naics_category && showRequiredValidation && (
            <p className="acc-error-message">Business Type is required</p>
          )}
        </div>
      )}
      <LeaseAbstractionField
        key="tenantCountryName"
        fieldType={LeaseAbstractionFieldType.SELECT}
        isRequired
        fieldData={{
          data: modalFieldState.tenantCountryName,
          label: 'Country',
          errorMessage: (!modalFieldState.tenantCountryName.value && showPageValidation) ? 'Country is required' : fieldErrorMap.tenantCountryName,
          key: 'tenantCountryName',
          options: countries,
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler(
            'multipleFieldNames',
            {
              tenantCountryName: {
                ...modalFieldState.tenantCountryName,
                value: event.value,
              },
              tenantStateName: {
                ...modalFieldState.tenantStateName,
                value: '',
              },
              filteredStatesForTenant: {
                value: getFilteredStates(event.value),
              },
            },
            'multiple',
          );
          onCompanyChange({ target: { name: 'country', value: event.value, type: 'select-one' } });
        }}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.value,
            parent_key: 'tenant_buyer_info',
            child_key: modalFieldState.tenantCountryName.trackingID,
            stateVariable: 'tenantCountryName',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('multipleFieldNames', {
            tenantCountryName: { ...modalFieldState.tenantCountryName, value: extractedVal },
            tenantStateName: { ...modalFieldState.tenantStateName, value: '' },
            filteredStatesForTenant: { value: getFilteredStates(extractedVal) },
          }, 'multiple');
          patchTenantCompany({ country: extractedVal, territory: null });
        }}
        validateLeaseAbstractionFields={validateLeaseAbstractionFields}
      />
      <LeaseAbstractionField
        key="tenantAddressLine1"
        fieldType={LeaseAbstractionFieldType.TEXT}
        isRequired
        fieldData={{
          data: modalFieldState.tenantAddressLine1,
          label: 'Address Line 1',
          errorMessage: (!modalFieldState.tenantAddressLine1.value && showPageValidation) ? 'Address Line 1 is required' : fieldErrorMap.tenantAddressLine1,
          key: 'tenantAddressLine1',
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler('tenantAddressLine1', {
            value: event.target.value,
          });
          onCompanyChange({ target: { name: 'address', value: event.target.value, type: 'text' } });
        }}
        onBlur={() => onCompanyBlur({ target: { name: 'address', value: modalFieldState.tenantAddressLine1.value, type: 'text' } })}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.target.value,
            parent_key: 'tenant_buyer_info',
            child_key: modalFieldState.tenantAddressLine1.trackingID,
            stateVariable: 'tenantAddressLine1',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('tenantAddressLine1', { value: extractedVal });
          patchTenantCompany({ address: extractedVal });
        }}
        validateLeaseAbstractionFields={validateLeaseAbstractionFields}
      />
      <LeaseAbstractionField
        key="tenantAddressLine2"
        fieldType={LeaseAbstractionFieldType.TEXT}
        fieldData={{
          data: modalFieldState.tenantAddressLine2,
          label: 'Address Line 2',
          errorMessage: fieldErrorMap.tenantAddressLine2,
          key: 'tenantAddressLine2',
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler('tenantAddressLine2', {
            value: event.target.value,
          });
          onCompanyChange({ target: { name: 'address2', value: event.target.value, type: 'text' } });
        }}
        onBlur={() => onCompanyBlur({ target: { name: 'address2', value: modalFieldState.tenantAddressLine2.value, type: 'text' } })}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.target.value,
            parent_key: 'tenant_buyer_info',
            child_key: modalFieldState.tenantAddressLine2.trackingID,
            stateVariable: 'tenantAddressLine2',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('tenantAddressLine2', { value: extractedVal });
          patchTenantCompany({ address2: extractedVal });
        }}
        validateLeaseAbstractionFields={validateLeaseAbstractionFields}
      />
      <LeaseAbstractionField
        key="tenantCity"
        fieldType={LeaseAbstractionFieldType.TEXT}
        isRequired
        fieldData={{
          data: modalFieldState.tenantCity,
          label: 'City',
          errorMessage: (!modalFieldState.tenantCity.value && showPageValidation) ? 'City is required' : fieldErrorMap.tenantCity,
          key: 'tenantCity',
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler('tenantCity', {
            value: event.target.value,
          });
          onCompanyChange({ target: { name: 'city', value: event.target.value, type: 'text' } });
        }}
        onBlur={() => onCompanyBlur({ target: { name: 'city', value: modalFieldState.tenantCity.value, type: 'text' } })}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.target.value,
            parent_key: 'tenant_buyer_info',
            child_key: modalFieldState.tenantCity.trackingID,
            stateVariable: 'tenantCity',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('tenantCity', { value: extractedVal });
          patchTenantCompany({ city: extractedVal });
        }}
        validateLeaseAbstractionFields={validateLeaseAbstractionFields}
      />
      <LeaseAbstractionField
        key="tenantStateName"
        fieldType={LeaseAbstractionFieldType.SELECT}
        isRequired
        fieldData={{
          data: modalFieldState.tenantStateName,
          label: 'State',
          errorMessage: (!modalFieldState.tenantStateName.value && showPageValidation) ? 'State is required' : fieldErrorMap.tenantStateName,
          key: 'tenantStateName',
          options: modalFieldState.filteredStatesForTenant.value,
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler('tenantStateName', { value: event.value });
          onCompanyChange({ target: { name: 'territory', value: event.value, type: 'select-one' } });
        }}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.value,
            parent_key: 'tenant_buyer_info',
            child_key: modalFieldState.tenantStateName.trackingID,
            stateVariable: 'tenantStateName',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          const stateEntry = states.find(s => s.display_name === extractedVal || s.value === extractedVal);
          const abbrev = stateEntry ? stateEntry.value : extractedVal;
          modalFieldChangeHandler('tenantStateName', { value: abbrev, chipDisplayValue: extractedVal });
          patchTenantCompany({ territory: abbrev });
        }}
        validateLeaseAbstractionFields={validateLeaseAbstractionFields}
      />
      <LeaseAbstractionField
        key="tenantZip"
        fieldType={LeaseAbstractionFieldType.TEXT}
        isRequired
        fieldData={{
          data: modalFieldState.tenantZip,
          label: 'Zip',
          errorMessage: (!modalFieldState.tenantZip.value && showPageValidation) ? 'Zip is required' : fieldErrorMap.tenantZip,
          key: 'tenantZip',
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler('tenantZip', {
            value: event.target.value,
          });
          onCompanyChange({ target: { name: 'postal', value: event.target.value, type: 'text' } });
        }}
        onBlur={() => onCompanyBlur({ target: { name: 'postal', value: modalFieldState.tenantZip.value, type: 'text' } })}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.target.value,
            parent_key: 'tenant_buyer_info',
            child_key: modalFieldState.tenantZip.trackingID,
            stateVariable: 'tenantZip',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('tenantZip', { value: extractedVal });
          patchTenantCompany({ postal: extractedVal });
        }}
        validateLeaseAbstractionFields={validateLeaseAbstractionFields}
      />
      {/* GWS Client */}
      {isEnhancedFlow && (
        <div className="file-modal-group-full">
          <label className="file-modal-label">GWS Client</label>
          <input
            type="text"
            className="acc-input-text"
            name="gws_client"
            value={company?.gws_client || ''}
            onChange={onCompanyChange}
            onBlur={onCompanyBlur}
          />
        </div>
      )}
      {/* Parent Company */}
      {isEnhancedFlow && (
        <div className="file-modal-group-full">
          <label className="file-modal-label">Parent Company</label>
          <input
            type="text"
            className="acc-input-text"
            name="parent_company"
            value={company?.parent_company || ''}
            onChange={onCompanyChange}
            onBlur={onCompanyBlur}
          />
        </div>
      )}
      {/* Tenant Credit Rating */}
      {isEnhancedFlow && (
        <div className="file-modal-group-full">
          <label className="file-modal-label">Tenant Credit Rating</label>
          <Select
            defaultOption={company?.credit_rating ? `Class ${company.credit_rating}` : 'None'}
            options={creditRatingData}
            onChange={(e) => onCompanyChange({ target: { name: 'credit_rating', value: e.value, type: 'select' } })}
          />
        </div>
      )}
      {/* Is Lessee our client? */}
      {deal && (deal.deal_type === 'lease' || deal.deal_type === 'sale') && (
        <div className="file-modal-group-full">
          <Checkbox checked={!!isTenantOurClient} disabled>
            Is {deal.deal_type === 'lease' ? 'Lessee' : 'Buyer'} our client?
          </Checkbox>
        </div>
      )}
      <p className="file-modal-sub-heading">Point of Contact</p>
      {isEnhancedFlow && (() => {
        const cards = dealContacts.filter(c =>
          c.contact_type !== 'landlord' &&
          (c.given_name || c.surname) &&
          `${c.given_name || ''} ${c.surname || ''}`.trim() !== `${modalFieldState.tenantPOCFirstName?.value || ''} ${modalFieldState.tenantPOCLastName?.value || ''}`.trim()
        );
        return cards.length > 0 ? (
          <ul className="acc-deal-property-list">
            {cards.map(c => (
              // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
              <li
                key={c.id}
                className="acc-deal-property-card"
                onClick={() => handleTenantContactSelect(c)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleTenantContactSelect(c); }}
              >
                <div className="acc-deal-property-card__text">
                  <h4>{[c.given_name, c.surname].filter(Boolean).join(' ')}</h4>
                  <p>{c.job_title || (c.email_addresses && c.email_addresses[0]?.address) || ''}</p>
                </div>
                <span className="acc-add-icon">
                  <Icon iconName="plus-circle-outline" size="large" />
                </span>
              </li>
            ))}
          </ul>
        ) : null;
      })()}
      <p className="file-modal-note">Any changes made will be reflected on the Deal Contacts page.</p>
      {/* First Name */}
      {isEnhancedFlow ? (
        <div className="file-modal-group-container">
          <div className={`file-modal-group${tenantPOCFirstNameError ? ' acc-field-has-error' : ''}`} style={{ minWidth: 0 }}>
            <label className="acc-field-label">
              {isTenantPocRequired && <span className="acc-required-asterisk">* </span>}First Name
            </label>
            <ContactAutocomplete
              initSearchTerm={modalFieldState.tenantPOCFirstName.value || ''}
              onChange={(input) => {
                if (typeof input === 'string') {
                  modalFieldChangeHandler('tenantPOCFirstName', { value: input });
                } else {
                  suppressTenantFirstNameBlurPatchRef.current = true;
                  handleTenantContactSelect(input);
                }
              }}
              onBlur={() => {
                if (suppressTenantFirstNameBlurPatchRef.current) {
                  suppressTenantFirstNameBlurPatchRef.current = false;
                  return;
                }
                patchTenantContact({ given_name: modalFieldState.tenantPOCFirstName.value });
              }}
              errorMessage={tenantPOCFirstNameError}
              placeholder="Search Contacts"
            />
          </div>
          {modalFieldState.tenantPOCFirstName.toolTip?.pageIndex?.length > 0 && (
            <div className="acc-extraction-chip">
              <span className="acc-extraction-chip__icon" aria-hidden="true" />
              {String(modalFieldState.tenantPOCFirstName.extractedValue || '').trim().toLowerCase() !== String(modalFieldState.tenantPOCFirstName.value || '').trim().toLowerCase() ? (
                <button
                  type="button"
                  className="acc-extraction-chip__accept"
                  title="Click to replace with extracted value"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    modalFieldChangeHandler('tenantPOCFirstName', { value: modalFieldState.tenantPOCFirstName.extractedValue });
                    patchTenantContact({ given_name: modalFieldState.tenantPOCFirstName.extractedValue });
                  }}
                >
                  {modalFieldState.tenantPOCFirstName.extractedValue}
                </button>
              ) : (
                <span className="acc-extraction-chip__value">{modalFieldState.tenantPOCFirstName.extractedValue}</span>
              )}
              <button
                type="button"
                className="acc-extraction-chip__page"
                onClick={() => findFieldRef('tenantPOCFirstName', parseInt(modalFieldState.tenantPOCFirstName.toolTip.pageIndex, 10))}
              >
                Page {modalFieldState.tenantPOCFirstName.toolTip.pageIndex}
              </button>
            </div>
          )}
        </div>
      ) : (
        <LeaseAbstractionField
          key="tenantPOCFirstName"
          fieldType={LeaseAbstractionFieldType.TEXT}
          fieldData={{
            data: modalFieldState.tenantPOCFirstName,
            label: 'First Name',
            errorMessage: fieldErrorMap.tenantPOCFirstName,
            key: 'tenantPOCFirstName',
          }}
          fieldChangeHandler={(event) => {
            modalFieldChangeHandler('tenantPOCFirstName', { value: event.target.value });
          }}
          individualFieldChangeHandler={(event) => {
            individualModalFieldChangeHandler({
              child_value: event.target.value,
              parent_key: 'tenant_buyer_info',
              child_key: modalFieldState.tenantPOCFirstName.trackingID,
              stateVariable: 'tenantPOCFirstName',
            });
          }}
        />
      )}
      {/* Last Name — with contact search autocomplete */}
      {/* Last Name */}
      {isEnhancedFlow ? (
        <div className="file-modal-group-container">
          <div className={`file-modal-group${tenantPOCLastNameError ? ' acc-field-has-error' : ''}`} style={{ minWidth: 0 }}>
            <label className="acc-field-label">
              {isTenantPocRequired && <span className="acc-required-asterisk">* </span>}Last Name
            </label>
            <ContactAutocomplete
              initSearchTerm={modalFieldState.tenantPOCLastName.value || ''}
              onChange={(input) => {
                if (typeof input === 'string') {
                  modalFieldChangeHandler('tenantPOCLastName', { value: input });
                } else {
                  suppressTenantLastNameBlurPatchRef.current = true;
                  handleTenantContactSelect(input);
                }
              }}
              onBlur={() => {
                if (suppressTenantLastNameBlurPatchRef.current) {
                  suppressTenantLastNameBlurPatchRef.current = false;
                  return;
                }
                patchTenantContact({ surname: modalFieldState.tenantPOCLastName.value });
              }}
              errorMessage={tenantPOCLastNameError}
              placeholder="Search Contacts"
            />
          </div>
          {modalFieldState.tenantPOCLastName.toolTip?.pageIndex?.length > 0 && (
            <div className="acc-extraction-chip">
              <span className="acc-extraction-chip__icon" aria-hidden="true" />
              {String(modalFieldState.tenantPOCLastName.extractedValue || '').trim().toLowerCase() !== String(modalFieldState.tenantPOCLastName.value || '').trim().toLowerCase() ? (
                <button
                  type="button"
                  className="acc-extraction-chip__accept"
                  title="Click to replace with extracted value"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    modalFieldChangeHandler('tenantPOCLastName', { value: modalFieldState.tenantPOCLastName.extractedValue });
                    patchTenantContact({ surname: modalFieldState.tenantPOCLastName.extractedValue });
                  }}
                >
                  {modalFieldState.tenantPOCLastName.extractedValue}
                </button>
              ) : (
                <span className="acc-extraction-chip__value">{modalFieldState.tenantPOCLastName.extractedValue}</span>
              )}
              <button
                type="button"
                className="acc-extraction-chip__page"
                onClick={() => findFieldRef('tenantPOCLastName', parseInt(modalFieldState.tenantPOCLastName.toolTip.pageIndex, 10))}
              >
                Page {modalFieldState.tenantPOCLastName.toolTip.pageIndex}
              </button>
            </div>
          )}
        </div>
      ) : (
        <LeaseAbstractionField
          key="tenantPOCLastName"
          fieldType={LeaseAbstractionFieldType.TEXT}
          fieldData={{
            data: modalFieldState.tenantPOCLastName,
            label: 'Last Name',
            errorMessage: fieldErrorMap.tenantPOCLastName,
            key: 'tenantPOCLastName',
          }}
          fieldChangeHandler={(event) => {
            modalFieldChangeHandler('tenantPOCLastName', { value: event.target.value });
          }}
          individualFieldChangeHandler={(event) => {
            individualModalFieldChangeHandler({
              child_value: event.target.value,
              parent_key: 'tenant_buyer_info',
              child_key: modalFieldState.tenantPOCLastName.trackingID,
              stateVariable: 'tenantPOCLastName',
            });
          }}
        />
      )}
      <LeaseAbstractionField
        key="tenantPOCPhone"
        fieldType={LeaseAbstractionFieldType.PHONE}
        isRequired={isTenantPocRequired}
        fieldData={{
          data: modalFieldState.tenantPOCPhone,
          label: 'Phone',
          errorMessage: tenantPOCPhoneError,
          key: 'tenantPOCPhone',
        }}
        fieldChangeHandler={(value, isValid) => {
          modalFieldChangeHandler(
            'multipleFieldNames',
            {
              tenantPOCPhone: {
                ...modalFieldState.tenantPOCPhone,
                value,
              },
              tenantPOCPhoneValid: isValid,
            },
            'multiple',
          );
          if (isValid && value) {
            patchTenantContact({
              phone_numbers: [{ type: 'work', number: value, name: '', default: true, isValid: true }],
            });
          } else if (!value) {
            patchTenantContact({ phone_numbers: [] });
          }
        }}
        individualFieldChangeHandler={(value) => {
          individualModalFieldChangeHandler({
            child_value: value,
            parent_key: 'tenant_buyer_info',
            child_key: modalFieldState.tenantPOCPhone.trackingID,
            stateVariable: 'tenantPOCPhone',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('multipleFieldNames', {
            tenantPOCPhone: { ...modalFieldState.tenantPOCPhone, value: extractedVal },
            tenantPOCPhoneValid: true,
          }, 'multiple');
          patchTenantContact({
            phone_numbers: [{
              type: 'work', number: extractedVal, name: '', default: true, isValid: true,
            }],
          });
        }}
        validateLeaseAbstractionFields={validateLeaseAbstractionFields}
      />
      <LeaseAbstractionField
        key="tenantPOCEmail"
        fieldType={LeaseAbstractionFieldType.TEXT}
        isRequired={isTenantPocRequired}
        fieldData={{
          data: modalFieldState.tenantPOCEmail,
          label: 'E-Mail Address',
          errorMessage: tenantPOCEmailError,
          key: 'tenantPOCEmail',
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler('tenantPOCEmail', {
            value: event.target.value,
          });
        }}
        onBlur={() => patchTenantContact({
          email_addresses: [{ type: 'work', address: modalFieldState.tenantPOCEmail.value, name: '', default: true, isValid: true }],
        })}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.target.value,
            parent_key: 'tenant_buyer_info',
            child_key: modalFieldState.tenantPOCEmail.trackingID,
            stateVariable: 'tenantPOCEmail',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('tenantPOCEmail', { value: extractedVal });
          patchTenantContact({
            email_addresses: [{
              type: 'work', address: extractedVal, name: '', default: true, isValid: true,
            }],
          });
        }}
        validateLeaseAbstractionFields={validateLeaseAbstractionFields}
      />
      <LeaseAbstractionField
        key="tenantPOCJobTitle"
        fieldType={LeaseAbstractionFieldType.TEXT}
        fieldData={{
          data: modalFieldState.tenantPOCJobTitle,
          label: 'Job Title',
          errorMessage: fieldErrorMap.tenantPOCJobTitle,
          key: 'tenantPOCJobTitle',
        }}
        fieldChangeHandler={(event) => {
          modalFieldChangeHandler('tenantPOCJobTitle', {
            value: event.target.value,
          });
        }}
        onBlur={() => patchTenantContact({ job_title: modalFieldState.tenantPOCJobTitle.value })}
        individualFieldChangeHandler={(event) => {
          individualModalFieldChangeHandler({
            child_value: event.target.value,
            parent_key: 'tenant_buyer_info',
            child_key: modalFieldState.tenantPOCJobTitle.trackingID,
            stateVariable: 'tenantPOCJobTitle',
          });
        }}
        acceptFieldChangeHandler={(extractedVal) => {
          modalFieldChangeHandler('tenantPOCJobTitle', { value: extractedVal });
          patchTenantContact({ job_title: extractedVal });
        }}
        validateLeaseAbstractionFields={validateLeaseAbstractionFields}
      />
      {/* Is the tenant relocating as part of this lease? */}
      {deal && deal.deal_type === 'lease' && (
        <>
          <div className="file-modal-group-full">
            <p className="file-modal-label">Is the tenant relocating as part of this lease?</p>
            <RadioGroup
              name="tenant-relocation"
              selectedValue={effectiveRelocating ? 'true' : 'false'}
              onChange={handleRelocationChange}
              orientation="vertical"
            >
              <RadioButton value="false">No</RadioButton>
              <RadioButton value="true">Yes, the tenant is relocating</RadioButton>
            </RadioGroup>
          </div>
          {effectiveRelocating && priorProperty && (
            <div>
              <p className="file-modal-sub-heading">Tenant Prior Address</p>
              {/* Country */}
              <div className="file-modal-group-full">
                <label className="file-modal-label">{!isEarlyInvoice && '* '}Country</label>
                <Select
                  defaultOption={priorProperty.country || 'United States'}
                  options={countries}
                  onChange={(e) => onPriorPropertyChange(priorProperty.id)({ target: { type: 'select', name: 'country', value: e.value } })}
                  errorMessage={!priorProperty.country && showRequiredValidation ? ' ' : ''}
                />
                {!priorProperty.country && showRequiredValidation && (
                  <p className="acc-error-message">Country is required</p>
                )}
              </div>
              {/* Address */}
              <div className="file-modal-group-full">
                <label className="file-modal-label">{!isEarlyInvoice && '* '}Address</label>
                <input
                  type="text"
                  className={`acc-input-text${!priorProperty.address && showRequiredValidation ? ' acc-input-error' : ''}`}
                  name="address"
                  value={priorProperty.address || ''}
                  onChange={onPriorPropertyChange(priorProperty.id)}
                  onBlur={onPriorPropertyBlur(priorProperty.id)}
                />
                {!priorProperty.address && showRequiredValidation && (
                  <p className="acc-error-message">Address is required</p>
                )}
              </div>
              {/* City */}
              <div className="file-modal-group-full">
                <label className="file-modal-label">{!isEarlyInvoice && '* '}City</label>
                <input
                  type="text"
                  className={`acc-input-text${!priorProperty.city && showRequiredValidation ? ' acc-input-error' : ''}`}
                  name="city"
                  value={priorProperty.city || ''}
                  onChange={onPriorPropertyChange(priorProperty.id)}
                  onBlur={onPriorPropertyBlur(priorProperty.id)}
                />
                {!priorProperty.city && showRequiredValidation && (
                  <p className="acc-error-message">City is required</p>
                )}
              </div>
              {/* State */}
              <div className="file-modal-group-full">
                <label className="file-modal-label">{!isEarlyInvoice && '* '}State</label>
                <Select
                  defaultOption={priorProperty.state_abbreviation || 'Select'}
                  showDefault={!priorProperty.state_abbreviation}
                  options={priorFilteredStates}
                  onChange={(e) => onPriorPropertyChange(priorProperty.id)({ target: { type: 'select', name: 'state_abbreviation', value: e.value } })}
                  errorMessage={!priorProperty.state_abbreviation && showRequiredValidation ? ' ' : ''}
                />
                {!priorProperty.state_abbreviation && showRequiredValidation && (
                  <p className="acc-error-message">State is required</p>
                )}
              </div>
              {/* Zip */}
              <div className="file-modal-group-full">
                <label className="file-modal-label">{!isEarlyInvoice && '* '}Zip</label>
                <input
                  type="text"
                  className={`acc-input-text${!priorProperty.postal_code && showRequiredValidation ? ' acc-input-error' : ''}`}
                  name="postal_code"
                  value={priorProperty.postal_code || ''}
                  onChange={onPriorPropertyChange(priorProperty.id)}
                  onBlur={onPriorPropertyBlur(priorProperty.id)}
                />
                {!priorProperty.postal_code && showRequiredValidation && (
                  <p className="acc-error-message">Zip is required</p>
                )}
              </div>
              {/* Prior rent area */}
              <div className="file-modal-group-full">
                <label className="file-modal-label">Prior rent area (SqFt)</label>
                <input
                  type="number"
                  className="acc-input-text"
                  name="space_total_square_feet"
                  value={(priorProperty?.spaces || [])[0]?.space_total_square_feet || ''}
                  onChange={onPriorSpaceChange(priorProperty.id, 0)}
                  onBlur={onPriorSpaceBlur(priorProperty.id, 0)}
                />
              </div>
              {/* Was the prior lease a percentage lease? */}
              <div className="file-modal-group-full">
                <p className="file-modal-label">Was the prior lease a percentage lease (for retail spaces only)?</p>
                <RadioGroup
                  name="percentage_rent"
                  selectedValue={priorProperty.percentage_rent === true || priorProperty.percentage_rent === 'true' ? 'true' : 'false'}
                  onChange={(e) => onPriorPropertyChange(priorProperty.id)({ target: { name: 'percentage_rent', value: e.target.value, type: 'radio' } })}
                  orientation="vertical"
                >
                  <RadioButton value="false">No</RadioButton>
                  <RadioButton value="true">Yes, it was a percentage lease</RadioButton>
                </RadioGroup>
              </div>
            </div>
          )}
        </>
      )}
      {showRelocationModal && (
        <ConfirmationModal
          className="deal-file-delete-confirmation tenant-modal voucher-modal"
          showModal={showRelocationModal}
        >
          <div className="header-modal">
            <h1 className="header-content conf-header">Confirmation</h1>
            <IconButton
              iconName="close-circle"
              className="icon-circle"
              variant="basic"
              onClick={() => setShowRelocationModal(false)}
            />
          </div>
          <div className="conf-text">
            <p>This will remove all data from the Tenant Prior Address section. Are you sure you want to make this change?</p>
          </div>
          <footer className="actions">
            <Button
              className="cancel-btn"
              variant="secondary"
              onClick={() => setShowRelocationModal(false)}
            >
              Cancel
            </Button>
            <Button
              className="upload-btn ok-btn"
              onClick={() => {
                setShowRelocationModal(false);
                setLocalRelocating(false);
                if (priorProperty) onPriorPropertyDelete(priorProperty.id);
              }}
            >
              Ok
            </Button>
          </footer>
        </ConfirmationModal>
      )}
    </FileUploadAccordian>
  );
};

TenantInformation.propTypes = {
  forceExpanded: PropTypes.bool,
  isLoaded: PropTypes.bool,
  onExpand: PropTypes.func,
  storageKey: PropTypes.string,
};

TenantInformation.defaultProps = {
  forceExpanded: false,
  isLoaded: false,
  onExpand: () => {},
  storageKey: '',
};

export default TenantInformation;

